import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, Button, Dialog, Input, Textarea, useToast, type BadgeTone } from '@artosera/ui';
import { usePeut } from '../../../auth/usePeut.js';
import { modifierOuverture, transitionOuverture } from '../../../lib/api.js';
import { formaterDate } from '../../../lib/format.js';
import {
  SOUS_ETAPES,
  dateEnSaisie,
  estClos,
  etapeCochee,
  formaterJour,
} from '../../../lib/ouvertures.js';
import type { DossierOuverture, EtatOuverture } from '../../../types.js';
import formStyles from '../../../styles/form.module.css';
import pageStyles from '../../../styles/page.module.css';
import styles from '../Ouvertures.module.css';
import type { Ecriture } from '../DossierDetail.js';

const TONE_SOUS_ETAPE: Record<string, BadgeTone> = {
  fait: 'available',
  sans_objet: 'neutral',
  en_cours: 'reserved',
  refuse: 'sold',
  afaire: 'neutral',
};

/** La synthèse : les gestes que l'API autorise, la liste de contrôle, les jalons, les notes. */
export function Synthese({
  dossier,
  ecrire,
  resiliation,
  surResiliation,
}: {
  dossier: DossierOuverture;
  ecrire: Ecriture;
  /** L'abandon vient de rendre `resiliation.aFaire` : la galerie reste à résilier. */
  resiliation: string | null;
  surResiliation: (galleryId: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const { toast } = useToast();
  const peutGerer = peut('console:manage-onboarding') && !estClos(dossier.etat);

  const [abandon, setAbandon] = useState(false);
  const [motif, setMotif] = useState('');
  const [motifErreur, setMotifErreur] = useState(false);
  const [recette, setRecette] = useState(false);
  const [motifRecette, setMotifRecette] = useState('');
  const [bascule, setBascule] = useState(dateEnSaisie(dossier.jalons.basculePrevueLe));
  const [note, setNote] = useState('');

  const aResilier =
    resiliation ?? (dossier.etat === 'abandonne' ? (dossier.galleryId ?? null) : null);

  // Seuls les boutons que l'API propose (`transitionsPossibles`) existent.
  const autres = dossier.transitionsPossibles.filter((vers) => vers !== 'abandonne');
  const peutAbandonner = dossier.transitionsPossibles.includes('abandonne');

  const lancer = async (vers: EtatOuverture, raison?: string): Promise<boolean> =>
    ecrire(async (courant) => {
      const reponse = await transitionOuverture(courant.id, {
        vers,
        revision: courant.revision,
        ...(raison === undefined ? {} : { motif: raison }),
      });
      if (reponse.resiliation) surResiliation(reponse.resiliation.galleryId);
      toast({
        title: t('ouvertures.fiche.synthese.succes', { etat: t(`ouvertures.etat.${vers}`) }),
        closeLabel: t('saisies.suppression.fermer'),
      });
      return reponse;
    });

  const confirmerAbandon = async (): Promise<void> => {
    if (motif.trim().length < 3) {
      setMotifErreur(true);
      return;
    }
    const fait = await lancer('abandonne', motif.trim());
    if (fait) {
      setAbandon(false);
      setMotif('');
    }
  };

  const confirmerRecette = async (): Promise<void> => {
    const fait = await lancer(
      'recette',
      motifRecette.trim() === '' ? undefined : motifRecette.trim(),
    );
    // La boîte se ferme pour montrer l'erreur de la garde ; le motif reste saisi.
    setRecette(false);
    if (fait) setMotifRecette('');
  };

  const enregistrerBascule = (): Promise<boolean> =>
    ecrire(async (courant) => {
      const mis = await modifierOuverture(courant.id, {
        revision: courant.revision,
        jalons: { basculePrevueLe: bascule === '' ? null : bascule },
      });
      toast({
        title: t('ouvertures.fiche.synthese.jalonEnregistre'),
        closeLabel: t('saisies.suppression.fermer'),
      });
      return mis;
    });

  const ajouterNote = async (): Promise<void> => {
    if (note.trim() === '') return;
    const fait = await ecrire(async (courant) => {
      const mis = await modifierOuverture(courant.id, {
        revision: courant.revision,
        note: note.trim(),
      });
      toast({
        title: t('ouvertures.fiche.synthese.noteEnregistree'),
        closeLabel: t('saisies.suppression.fermer'),
      });
      return mis;
    });
    if (fait) setNote('');
  };

  const jalons: Array<[string, string | null]> = [
    [t('ouvertures.fiche.synthese.basculeEffectuee'), dossier.jalons.basculeEffectueeLe],
    [t('ouvertures.fiche.synthese.scelle'), dossier.jalons.scelleLe],
    [t('ouvertures.fiche.synthese.purge'), dossier.jalons.purgeSourcesLe],
  ];

  return (
    <div className={styles.pile}>
      {aResilier ? (
        <div className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`} role="status">
          <span>
            <strong>{t('ouvertures.fiche.synthese.resiliation.titre')}</strong>{' '}
            {t('ouvertures.fiche.synthese.resiliation.texte')}
          </span>
          <Link to={`/galeries/${aResilier}/resiliation`} className="lien-cible">
            {t('ouvertures.fiche.synthese.resiliation.lien')}
          </Link>
        </div>
      ) : null}

      <div className={styles.deuxColonnes}>
        <section className={pageStyles.panneau} aria-labelledby="ouv-transitions">
          <div className={pageStyles.sectionTitre}>
            <h2 id="ouv-transitions">{t('ouvertures.fiche.synthese.transitions')}</h2>
          </div>
          {!peut('console:manage-onboarding') ? (
            <p className={formStyles.aide}>{t('ouvertures.fiche.synthese.lectureSeule')}</p>
          ) : null}
          {peutGerer && (autres.length > 0 || peutAbandonner) ? (
            <div className={styles.boutons}>
              {autres.map((vers, rang) => (
                <Button
                  key={vers}
                  variant={rang === 0 ? 'primary' : 'secondary'}
                  onClick={() => (vers === 'recette' ? setRecette(true) : void lancer(vers))}
                >
                  {t(`ouvertures.fiche.synthese.transition.${vers}`)}
                </Button>
              ))}
              {peutAbandonner ? (
                <Button variant="danger" onClick={() => setAbandon(true)}>
                  {t('ouvertures.fiche.synthese.transition.abandonne')}
                </Button>
              ) : null}
            </div>
          ) : (
            <p className={formStyles.aide}>{t('ouvertures.fiche.synthese.aucuneTransition')}</p>
          )}
        </section>

        <section className={pageStyles.panneau} aria-labelledby="ouv-jalons">
          <div className={pageStyles.sectionTitre}>
            <h2 id="ouv-jalons">{t('ouvertures.fiche.synthese.jalons')}</h2>
          </div>
          <div className={styles.ligneJalon}>
            <div className={formStyles.champ}>
              <label htmlFor="ouv-bascule">{t('ouvertures.fiche.synthese.basculePrevue')}</label>
              <Input
                id="ouv-bascule"
                type="date"
                mono
                disabled={!peutGerer}
                value={bascule}
                onChange={(evenement) => setBascule(evenement.target.value)}
              />
            </div>
            {peutGerer ? (
              <Button
                variant="secondary"
                disabled={bascule === dateEnSaisie(dossier.jalons.basculePrevueLe)}
                onClick={() => void enregistrerBascule()}
              >
                {t('ouvertures.fiche.synthese.enregistrerJalon')}
              </Button>
            ) : null}
          </div>
          <dl className={styles.faits}>
            {jalons.map(([cle, valeur]) => (
              <div key={cle} className={styles.faitsLigne}>
                <dt>{cle}</dt>
                <dd>{valeur ? formaterJour(valeur) : t('ouvertures.fiche.synthese.nonDate')}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <section className={pageStyles.panneau} aria-labelledby="ouv-controle">
        <div className={pageStyles.sectionTitre}>
          <h2 id="ouv-controle">{t('ouvertures.fiche.synthese.controle')}</h2>
        </div>
        <p className={formStyles.aide}>{t('ouvertures.fiche.synthese.controleTexte')}</p>
        <ul className={styles.sousEtapes}>
          {SOUS_ETAPES.map((cle) => {
            const coche = etapeCochee(dossier, cle);
            const etat = coche?.etat ?? 'afaire';
            return (
              <li
                key={cle}
                className={`${styles.sousEtape} ${
                  etat === 'fait' || etat === 'sans_objet' ? styles.sousEtapeFaite : ''
                } ${etat === 'refuse' ? styles.sousEtapeRefusee : ''}`}
                data-sous-etape={cle}
                data-etat={etat}
              >
                <span>
                  {t(`ouvertures.creation.sousEtape.${cle}`)}
                  {coche ? (
                    <span className={styles.sousEtapeNote}>
                      {[coche.par, formaterDate(coche.le, ''), coche.note]
                        .filter((morceau) => morceau !== '')
                        .join(' · ')}
                    </span>
                  ) : null}
                </span>
                <Badge tone={TONE_SOUS_ETAPE[etat]}>{t(`ouvertures.creation.etat.${etat}`)}</Badge>
              </li>
            );
          })}
        </ul>
      </section>

      <section className={pageStyles.panneau} aria-labelledby="ouv-notes">
        <div className={pageStyles.sectionTitre}>
          <h2 id="ouv-notes">{t('ouvertures.fiche.synthese.notes')}</h2>
        </div>
        {dossier.notes.length === 0 ? (
          <p className={formStyles.aide}>{t('ouvertures.fiche.synthese.notesVide')}</p>
        ) : (
          <ul className={styles.notes}>
            {dossier.notes.map((ligne, rang) => (
              <li key={rang}>
                {ligne.texte}
                <span className={styles.noteMeta}>
                  {t('ouvertures.fiche.synthese.noteParLe', {
                    par: ligne.par,
                    date: formaterDate(ligne.le, ''),
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
        {peutGerer ? (
          <div className={`${formStyles.champ} ${styles.espaceHaut}`}>
            <label htmlFor="ouv-note">{t('ouvertures.fiche.synthese.noteChamp')}</label>
            <Textarea
              id="ouv-note"
              rows={2}
              maxLength={2000}
              value={note}
              onChange={(evenement) => setNote(evenement.target.value)}
            />
            <div>
              <Button
                variant="secondary"
                disabled={note.trim() === ''}
                onClick={() => void ajouterNote()}
              >
                {t('ouvertures.fiche.synthese.noteAjouter')}
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <Dialog
        open={recette}
        onOpenChange={setRecette}
        title={t('ouvertures.fiche.synthese.recette.titre')}
        description={t('ouvertures.fiche.synthese.recette.texte')}
        closeLabel={t('ouvertures.fiche.synthese.recette.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRecette(false)}>
              {t('ouvertures.fiche.synthese.recette.annuler')}
            </Button>
            <Button onClick={() => void confirmerRecette()}>
              {t('ouvertures.fiche.synthese.recette.confirmer')}
            </Button>
          </>
        }
      >
        <div className={formStyles.champ}>
          <label htmlFor="ouv-motif-recette">{t('ouvertures.fiche.synthese.recette.motif')}</label>
          <Textarea
            id="ouv-motif-recette"
            rows={3}
            maxLength={1000}
            value={motifRecette}
            onChange={(evenement) => setMotifRecette(evenement.target.value)}
          />
        </div>
      </Dialog>

      <Dialog
        open={abandon}
        onOpenChange={(ouvert) => {
          setAbandon(ouvert);
          if (!ouvert) setMotifErreur(false);
        }}
        title={t('ouvertures.fiche.synthese.abandon.titre')}
        description={t('ouvertures.fiche.synthese.abandon.texte')}
        closeLabel={t('ouvertures.fiche.synthese.abandon.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAbandon(false)}>
              {t('ouvertures.fiche.synthese.abandon.annuler')}
            </Button>
            <Button variant="danger" onClick={() => void confirmerAbandon()}>
              {t('ouvertures.fiche.synthese.abandon.confirmer')}
            </Button>
          </>
        }
      >
        <div className={formStyles.champ}>
          <label htmlFor="ouv-motif">{t('ouvertures.fiche.synthese.abandon.motif')}</label>
          <Textarea
            id="ouv-motif"
            rows={3}
            maxLength={1000}
            value={motif}
            onChange={(evenement) => {
              setMotif(evenement.target.value);
              setMotifErreur(false);
            }}
            aria-invalid={motifErreur}
          />
          {motifErreur ? (
            <p className={formStyles.erreur} role="alert">
              {t('ouvertures.fiche.synthese.abandon.motifErreur')}
            </p>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}
